import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IPictureImage } from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/picture-model-props.interface';
import { EDataType, ImagePropsField } from '@src/slide/domain/ui-model/element-modeles/property/image-operation/constant/props-value';
import { ImageTypes } from '@src/slide/infrastructure/module/dependent/image/constant/image-type';
import { getUrlParam } from '@src/slide/infrastructure/module/independent/url/param-operation';
import { IPictureData, IPictureProps, ICTBlipFillProperties, DeltaOf } from '@tencent/slide-sdk-model';
import { DeepPartial, DeepReadonly } from 'ts-essentials';
import ImageQuickLookManager from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-quick-look-manager';
import { getUrlExtension } from '@src/slide/infrastructure/module/independent/url/extension';
import { colorStrToColorDmlA, colorValueToColorDmlA, getEgColorChoiceRgba } from '@src/slide/infrastructure/module/independent/color/transform';
import { BlipFillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/blip-fill/blip-fill-converter';
import _set from 'lodash/set';
import _get from 'lodash/get';
import _omit from 'lodash/omit';

// 黑白单位转换
const BI_LEVEL_COEFFICIENT = 1000;
// 冲蚀单位转换
const LUMINANCE_COEFFICIENT = 1000;
/**
 * 图片独有属性
 * 1、src以及backupSrc 2、imageType 3、透明度 4、图片校正/效果 5、裁剪 6、音视频 7、相对图片原始尺寸
 */
export class PictureInfoConverter {
    /**
     * 图片独有属性的sdk -> ui 映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IPictureProps>>,
        uiData: IPictureModel,
    ): void {
        // src以及backupSrc
        PictureInfoConverter.convertImageSrcDataSdkToUi(sdkData, uiData);
        // imageType
        PictureInfoConverter.convertImageTypeDataSdkToUi(sdkData, uiData);
        // 透明度
        PictureInfoConverter.convertImageAlphaDataSdkToUi(sdkData, uiData);
        // 裁剪
        PictureInfoConverter.convertImageRectDataSdkToUi(sdkData, uiData);
        // 音视频
        PictureInfoConverter.convertMediaDataSdkToUi(sdkData, uiData);
        // 相对图片原始尺寸比例缩放
        PictureInfoConverter.convertRelativeResizeDataSdkToUi(sdkData, uiData);
        // 平铺
        PictureInfoConverter.convertImageTileDataSdkToUi(sdkData, uiData);
        // 充满
        PictureInfoConverter.convertImageStretchDataSdkToUi(sdkData, uiData);

        if (!uiData.props?.style?.image) {
            _set(uiData, 'props.style.image', {});
        }
        // 校正、图片颜色（锐化/柔化、亮度、对比度、饱和度、色温）
        PictureInfoConverter.convertImageImgPropsDataSdkToUi(sdkData.blipFill, uiData.props.style.image);
        // 重新着色
        PictureInfoConverter.convertRecolorDataSdkToUi(sdkData.blipFill, uiData.props.style.image);
    }

    /**
     * 图片独有属性的ui-> sdk 映射
     * @param uiData
     * @param sdkData
     * @param rawUiData
     */
    public static convertPartialDataFromUiToSdk(
        uiData: DeepPartial<IPictureModel>,
        sdkData: DeltaOf<IPictureData>,
        rawUiData: IPictureModel,
    ): void {
        // src以及backupSrc
        PictureInfoConverter.convertImageSrcDataUiToSdk(uiData, sdkData);
        // 透明度
        PictureInfoConverter.convertImageAlphaDataUiToSdk(uiData, sdkData);
        // 裁剪
        PictureInfoConverter.convertImageRectDataUiToSdk(uiData, sdkData, rawUiData);
        // 音视频
        PictureInfoConverter.convertMediaDataUiToSdk(uiData, sdkData);
        // 相对图片原始尺寸比例缩放
        PictureInfoConverter.convertRelativeResizeDataUiToSdk(uiData, sdkData);

        sdkData.blipFill = sdkData.blipFill || {};
        // 校正、图片颜色（锐化/柔化、亮度、对比度、饱和度、色温）
        PictureInfoConverter.convertImageImgPropsDataUiToSdk(
            uiData.props?.style?.image,
            sdkData.blipFill,
            rawUiData.sdkData?.props?.blipFill as ICTBlipFillProperties,
        );
        // 重新着色
        PictureInfoConverter.convertRecolorDataUiToSdk(
            uiData.props?.style?.image,
            sdkData.blipFill,
        );

        const rawBlipFill = rawUiData?.sdkData?.props?.blipFill;
        if (!rawBlipFill?.egFillModeProperties?.stretch?.fillRect && !rawBlipFill?.egFillModeProperties?.tile) {
            // stretch和tile都不存在时，保证stretch节点有值，否则导出到office缩放会有问题
            _set(sdkData, 'blipFill.egFillModeProperties.stretch.fillRect', {});
        }
    }

    /**
     * 图片校正、图片效果转换（锐化/柔化、亮度、对比度、饱和度、色温）
     * @param sdkData
     * @param uiData
     */
    public static convertImageImgPropsDataSdkToUi(
        sdkData?: DeepReadonly<DeepPartial<ICTBlipFillProperties>>,
        uiData?: IPictureImage,
    ): void {
        const blip = sdkData?.blip;
        if (!blip || !uiData) {
            return;
        }
        Object.values(ImagePropsField).forEach(({ name, getSdkValue, defaultValue, coefficient }) => {
            const value = getSdkValue(blip);
            uiData[name] = value || value === 0 ? (value / coefficient) : defaultValue;
        });
    }

    /**
     * 重新着色
     * @param sdkData 来源sdk对象
     * @param uiData 目标ui对象
     */
    public static convertRecolorDataSdkToUi(
        sdkData?: DeepReadonly<DeepPartial<ICTBlipFillProperties>>,
        uiData?: IPictureImage,
    ): void {
        const blip = sdkData?.blip;
        if (!blip || !uiData) {
            return;
        }
        // 冲蚀效果的亮度、对比度的固定值
        const LUMINANCE_BRIGHTNESS = 70000;
        const LUMINANCE_CONTRAST = -70000;
        const biLevel = blip.biLevel?.[0]?.thresh;
        if (biLevel) {
            uiData.biLevel = biLevel / BI_LEVEL_COEFFICIENT;
        } else {
            delete uiData.biLevel;
        }
        const grayscl = blip.grayscl?.[0];
        if (grayscl === true || grayscl === false) {
            uiData.grayscl = grayscl;
        } else {
            delete uiData.grayscl;
        }
        const luminance = blip.lum?.[0];
        if (luminance?.bright === LUMINANCE_BRIGHTNESS && luminance?.contrast === LUMINANCE_CONTRAST) {
            uiData.luminance = {
                brightness: LUMINANCE_BRIGHTNESS / LUMINANCE_COEFFICIENT,
                contrast: LUMINANCE_CONTRAST / LUMINANCE_COEFFICIENT,
            };
        } else {
            delete uiData.luminance;
        }
        const { duotone } = blip;
        if (duotone?.[0]?.egColorChoice?.length) {
            const obj = {};
            duotone[0].egColorChoice.forEach((e, i) => {
                const color = getEgColorChoiceRgba(e?.srgbClr);
                obj[`clr${i + 1}`] = { srgb_color: color, colorValue: color };
            });
            uiData.duotone = obj as { clr1: any; clr2: any; };
        } else {
            delete uiData.duotone;
        }
    }

    /**
     * 图片校正、图片效果转换（锐化/柔化、亮度、对比度、饱和度、色温）
     * @param image
     * @param blipFill
     * @param rawBlipFill
     */
    public static convertImageImgPropsDataUiToSdk(
        image?: DeltaOf<IPictureImage>,
        blipFill?: DeltaOf<ICTBlipFillProperties>,
        rawBlipFill?: ICTBlipFillProperties,
    ): void {
        if (image && Object.keys(image).length && blipFill) {
            Object.values(ImagePropsField).forEach(({ name, getSdkPath, coefficient, dataType }) => {
                blipFill.blip = blipFill.blip || {};
                // 因为数据可能存在数组的不同index上，所以需要在原始数据上拿准确的路径
                const exactPath = getSdkPath(rawBlipFill?.blip ?? {});
                if (image[name] || image[name] === 0) {
                    let val;
                    if (dataType === EDataType.string) {
                        val = (image[name] * coefficient).toString();
                    } else {
                        val = image[name] * coefficient;
                    }
                    _set(blipFill.blip, exactPath, val);
                } else if (image[name] === null) {
                    _set(blipFill.blip, exactPath, null);
                } else {
                    const oldVal = _get(rawBlipFill?.blip, exactPath);
                    if (oldVal || oldVal === 0) {
                        _set(blipFill.blip, exactPath, oldVal);
                    }
                }
            });
        }
    }

    /**
     * 重新着色
     * @param image
     * @param blipFill
     */
    // eslint-disable-next-line complexity
    public static convertRecolorDataUiToSdk(
        image?: DeltaOf<IPictureImage>,
        blipFill?: DeltaOf<ICTBlipFillProperties>,
    ): void {
        if (!blipFill) {
            return;
        }
        if (image === null) {
            _set(blipFill, 'blip.biLevel', null);
            _set(blipFill, 'blip.grayscl', null);
            _set(blipFill, 'blip.lum', null);
            _set(blipFill, 'blip.duotone', null);
        } else if (!image) {
            return;
        }
        const { biLevel } = image;
        if (biLevel) {
            _set(blipFill, 'blip.biLevel[0].thresh', biLevel * BI_LEVEL_COEFFICIENT);
        } else {
            _set(blipFill, 'blip.biLevel', null);
        }
        const { grayscl } = image;
        if (grayscl === true || grayscl === false) {
            _set(blipFill, 'blip.grayscl[0]', grayscl);
        } else if (grayscl === null) {
            _set(blipFill, 'blip.grayscl', null);
        }
        const { luminance } = image;
        if (luminance) {
            _set(blipFill, 'blip.lum[0]', {
                bright: (luminance.brightness || 0) * LUMINANCE_COEFFICIENT,
                contrast: (luminance.contrast || 0) * LUMINANCE_COEFFICIENT,
            });
        } else {
            _set(blipFill, 'blip.lum', null);
        }
        const { duotone } = image;
        if (duotone && Object.values(duotone).length) {
            const arr = Object.values(duotone)
                .filter(e => e?.colorValue)
                .map((e) => {
                    const egColorTransformMap = _omit(e || {}, ['colorType', 'colorValue']);
                    return typeof e?.colorValue === 'number' ? colorValueToColorDmlA(e?.colorValue, egColorTransformMap) : colorStrToColorDmlA(e?.colorValue, egColorTransformMap);
                });
            _set(blipFill, 'blip.duotone[0].egColorChoice', arr);
        } else {
            _set(blipFill, 'blip.duotone', null);
        }
    }

    /**
     * 图片src以及backupSrc转换
     * @param sdkData
     * @param uiData
     */
    private static convertImageSrcDataSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IPictureProps>>,
        uiData: IPictureModel,
    ): void {
        const blip = sdkData.blipFill?.blip;
        if (!blip) {
            return;
        }
        const nextUI = ImageQuickLookManager.getInstance().getNextUIDataFromSDKSrc(blip.embed);
        if (nextUI.forceHidden) {
            uiData.hidden = 1;
        }
        uiData.props.src = nextUI.src || '';

        // 针对 GIF 图片，备份的是首帧图片；针对其它设置了图片格式的图片，备份的是 WDP 原图
        const backupSrc = blip.extLst?.ext?.[0]?.imgProps?.[0]?.imgLayer?.embed;
        if (backupSrc) {
            const nextBackupUI = ImageQuickLookManager.getInstance().getNextUIDataFromSDKSrc(backupSrc);
            if (!nextBackupUI.forceHidden) {
                uiData.props.backupSrc = nextBackupUI.src;
            }
        }
    }

    /**
     * 图片平铺转换
     * @param sdkData
     * @param uiData
     */
    private static convertImageTileDataSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IPictureProps>>,
        uiData: IPictureModel,
    ): void {
        const tile = sdkData.blipFill?.egFillModeProperties?.tile;
        if (tile) {
            _set(uiData, 'props.style.tile', BlipFillConverter.convertTileFromSdkToUi(sdkData.blipFill));
        } else if (uiData?.props?.style?.tile) {
            delete uiData.props.style.tile;
        }
    }

    /**
     * 图片充满转换
     * @param sdkData
     * @param uiData
     */
    private static convertImageStretchDataSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IPictureProps>>,
        uiData: IPictureModel,
    ): void {
        const stretch = sdkData.blipFill?.egFillModeProperties?.stretch;
        if (stretch) {
            _set(uiData, 'props.style.stretch', BlipFillConverter.convertStretchFromSdkToUi(sdkData.blipFill));
        } else if (uiData?.props?.style?.stretch) {
            delete uiData.props.style.stretch;
        }
    }

    /**
     * 图片imageType转换
     * @param sdkData
     * @param uiData
     */
    private static convertImageTypeDataSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IPictureProps>>,
        uiData: IPictureModel,
    ): void {
        const blip = sdkData.blipFill?.blip;
        if (!blip) {
            return;
        }
        // @ts-ignore
        const { imageType } = sdkData;
        if (imageType) {
            if (imageType === 'svg+xml') {
                // imageType只给model上用，导出不涉及，而3.0的svg的imageType变成了svg+xml，为了兼容model上的判断逻辑，转成svg
                uiData.props.imageType = ImageTypes.SVG;
            } else {
                uiData.props.imageType = imageType;
            }
        } else if (uiData.props.src) {
            let subImageType: ImageTypes | string = ImageTypes.UNKNOWN;
            const ext = getUrlExtension(uiData.props.src);
            if (ext) {
                // 尝试根据 src 中的图片名称来找
                subImageType = ext.toLowerCase();
            } else if (getUrlParam(uiData.props.src, '_type')) {
                // 尝试根据 URL 中的 _type param 来找
                subImageType = getUrlParam(uiData.props.src, '_type');
            }
            uiData.props.imageType = subImageType;
        }
    }

    /**
     * 图片透明度转换
     * @param sdkData
     * @param uiData
     */
    private static convertImageAlphaDataSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IPictureProps>>,
        uiData: IPictureModel,
    ): void {
        const blip = sdkData.blipFill?.blip;
        if (!blip) {
            return;
        }
        const alpha = blip.alphaModFix?.[0]?.amt;
        _set(uiData, 'props.style.alpha', alpha || alpha === 0 ? alpha / 100000 : 1);
    }

    /**
     * 图片裁剪转换
     * @param sdkData
     * @param uiData
     */
    private static convertImageRectDataSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IPictureProps>>,
        uiData: IPictureModel,
    ): void {
        const rect = {
            b: 0, t: 0, l: 0, r: 0,
        };
        if (sdkData.blipFill?.srcRect) {
            rect.b = (sdkData.blipFill.srcRect.b || 0) / 100000;
            rect.t = (sdkData.blipFill.srcRect.t || 0) / 100000;
            rect.l = (sdkData.blipFill.srcRect.l || 0) / 100000;
            rect.r = (sdkData.blipFill.srcRect.r || 0) / 100000;
        }
        _set(uiData, 'props.style.srcRect', rect);
    }

    /**
     * 音视频数据转换
     * @param sdkData 来源sdk对象
     * @param uiData 目标ui对象
     */
    private static convertMediaDataSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IPictureProps>>,
        uiData: IPictureModel,
    ): void {
        // video存在外链形式，暂时屏蔽掉，以isExternal字段判断
        if (sdkData.video && !sdkData.video.isExternal) {
            uiData.video = sdkData.video;
        }
        if (sdkData.audio) {
            uiData.audio = sdkData.audio;
        }
        if (sdkData?.nvPr?.media) {
            uiData.props.media = sdkData?.nvPr?.media;
        }
    }

    /**
     * 相对图片原始尺寸
     * @param sdkData 来源sdk对象
     * @param uiData 目标ui对象
     */
    private static convertRelativeResizeDataSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IPictureProps>>,
        uiData: IPictureModel,
    ): void {
        const resize = sdkData.nvPr?.preferRelativeResize;
        if (resize === true || resize === false) {
            uiData.props.relativeResize = resize ? 1 : 0;
        }
    }

    /**
     * 图片src以及backupSrc转换
     * @param uiData
     * @param sdkData
     */
    private static convertImageSrcDataUiToSdk(
        uiData: DeepPartial<IPictureModel>,
        sdkData: DeltaOf<IPictureData>,
    ): void {
        if (uiData.props?.src) {
            const resource = ImageQuickLookManager.getInstance().getResourceByUrl(uiData.props.src);
            _set(sdkData, 'blipFill.blip.embed', resource ? resource.id : uiData.props.src);
        }
    }

    /**
     * 图片透明度转换
     * @param uiData
     * @param sdkData
     */
    private static convertImageAlphaDataUiToSdk(
        uiData: DeepPartial<IPictureModel>,
        sdkData: DeltaOf<IPictureData>,
    ): void {
        if (uiData.props?.style?.alpha || uiData.props?.style?.alpha === 0) {
            _set(sdkData, 'blipFill.blip.alphaModFix[0].amt', uiData.props.style.alpha * 100000);
        }
    }

    /**
     * 图片裁剪转换
     * @param uiData
     * @param sdkData
     * @param rawUiData
     */
    private static convertImageRectDataUiToSdk(
        uiData: DeepPartial<IPictureModel>,
        sdkData: DeltaOf<IPictureData>,
        rawUiData?: IPictureModel,
    ): void {
        if (uiData.props?.style?.srcRect) {
            const rect = {
                b: 0, t: 0, l: 0, r: 0,
            };
            rect.b = Math.round((uiData.props.style.srcRect.b || 0) * 100000);
            rect.t = Math.round((uiData.props.style.srcRect.t || 0) * 100000);
            rect.l = Math.round((uiData.props.style.srcRect.l || 0) * 100000);
            rect.r = Math.round((uiData.props.style.srcRect.r || 0) * 100000);
            _set(sdkData, 'blipFill.srcRect', rect);
            // 如果当前是平铺状态，则自动重置为充满
            if (rawUiData?.sdkData?.props?.blipFill?.egFillModeProperties?.tile) {
                _set(sdkData, 'blipFill.egFillModeProperties.tile', null);
            }
        }
    }

    /**
     * 音视频数据转换
     * @param uiData
     * @param sdkData
     */
    private static convertMediaDataUiToSdk(
        uiData: DeepPartial<IPictureModel>,
        sdkData: DeltaOf<IPictureData>,
    ): void {
        if (uiData.video?.link) {
            _set(sdkData, 'nvPr.egMedia.videoFile.link', uiData.video.link);
        }

        if (uiData.audio?.link) {
            _set(sdkData, 'nvPr.egMedia.audioFile.link', uiData.audio.link);
        }

        if (uiData.props?.media) {
            _set(sdkData, 'nvPr.media', uiData.props.media);
        }
    }

    /**
     * 相对图片原始尺寸
     * @param sdkData 来源sdk对象
     * @param uiData 目标ui对象
     */
    private static convertRelativeResizeDataUiToSdk(
        uiData: DeepPartial<IPictureModel>,
        sdkData: DeltaOf<IPictureData>,
    ): void {
        if (uiData.props?.relativeResize === 1 || uiData.props?.relativeResize === 0) {
            _set(sdkData, 'nvPr.preferRelativeResize', !!uiData.props.relativeResize);
        }
    }
}
